/**
 * Recompte d'existències (OBR-147) — funcions PURES, el mirall del que fan les RPC `create_stock_count`, `save_stock_count_lines`,
 * `add_stock_count_line`, `validate_stock_count` i `cancel_stock_count`.
 *
 * FLUX: Creació → Recompte → Diferències → Ajust.
 *  · En CREAR-lo es desa la foto de l'estoc teòric per Lot + Lloc de l'abast (`theoretical`).
 *  · En COMPTAR, cada línia té una quantitat comptada (o res: «no comptada»). Es pot confirmar la teòrica o escriure'n una altra.
 *  · Les DIFERÈNCIES són coincideix / falta / sobra, amb la diferència en quantitat.
 *  · En VALIDAR, cada línia comptada amb diferència genera UN ajust amb motiu `recount`; les que coincideixen no en generen.
 *
 * DECISIÓ D7 (docs/Pla_Implementacio_Existencies.md): NO es bloquegen moviments mentre el recompte és obert, i en validar l'ajust és
 * `comptat − saldo ACTUAL`, no `comptat − foto`: un moviment fet entre la foto i la validació no es compta dues vegades. Per això
 * cada línia porta DUES teòriques (la de la foto i la d'ara) i la pantalla avisa si han canviat. Aquí, la diferència que es mostra
 * és sempre la que s'ajustarà de debò: contra la teòrica d'ARA quan es coneix, i contra la de la foto mentre no es coneix.
 *
 * LÍMIT CONEGUT DE D7: un moviment fet DESPRÉS de comptar una línia (i abans de validar-la) tampoc no queda recollit: es compta 10,
 * algú en treu 3 i es valida, l'ajust és `10 − 7 = +3` i desfà aquell moviment legítim. D7 no bloqueja res i el servidor no sap
 * quan s'ha comptat cada línia contra quin saldo; només es pot AVISAR (la línia surt «canviada» i la validació diu quantes n'hi ha).
 * El que sí es fa bé és el gest «Confirma teòrica»: confirma el saldo d'ara ({@link confirmedQuantity}), no la foto.
 *
 * Les quantitats són en UNITAT BASE de l'article, com tot el llibre d'estoc. Pur: sense I/O, sense rellotge i sense atzar.
 */

import type { UserRole } from '../enums/index.js';
import {
  STOCK_ADJUST_ERROR_CODES,
  STOCK_ERROR_GENERIC_KEY,
  STOCK_ERROR_I18N_KEYS,
  stockErrorCode,
  validateAdjustment,
  validateAdjustmentLot,
  type StockAdjustErrorCode,
} from './stockAdjust.js';
import { STOCK_QUANTITY_EPSILON } from './stockImport.js';
import { STOCK_COUNT_ROLES } from './menuRoles.js';
import { OBRADOR_TIME_ZONE } from './expiry.js';

/** Qui pot crear, comptar, validar i cancel·lar un recompte: els mateixos que ajusten (D7). El servidor ho comprova a cada RPC. */
export function canManageStockCount(roles: ReadonlyArray<UserRole>): boolean {
  return roles.some((role) => (STOCK_COUNT_ROLES as readonly UserRole[]).includes(role));
}

// ── Vocabulari ────────────────────────────────────────────────────────────────────

/** L'abast d'un recompte: tot l'obrador, una zona, una secció, una ubicació o un article. Una zona o secció inclouen tot el que hi penja. */
export const STOCK_COUNT_SCOPE_TYPES = [
  'tenant',
  'zone',
  'section',
  'location',
  'article',
] as const;
export type StockCountScopeType = (typeof STOCK_COUNT_SCOPE_TYPES)[number];

export function isStockCountScopeType(value: string): value is StockCountScopeType {
  return (STOCK_COUNT_SCOPE_TYPES as readonly string[]).includes(value);
}

export const STOCK_COUNT_STATUSES = ['open', 'validated', 'cancelled'] as const;
export type StockCountStatus = (typeof STOCK_COUNT_STATUSES)[number];

/** Les línies que un recompte pot portar (la foto d'un abast més gran es rebutja: `stock_count_too_large`). */
export const STOCK_COUNT_MAX_LINES = 3000;

/** Les línies que `save_stock_count_lines` accepta en una sola crida. */
export const STOCK_COUNT_SAVE_BATCH_LIMIT = 5000;

/** El motiu dels ajustos d'un recompte (ja existeix a `movement_reason`: no se n'afegeix cap valor). */
export const STOCK_COUNT_REASON = 'recount' as const;

/** Un recompte només es pot tocar mentre és obert. */
export function isStockCountOpen(status: StockCountStatus): boolean {
  return status === 'open';
}

// ── L'estat d'una línia ──────────────────────────────────────────────────────────────

/** Pendent (no comptada), coincideix, falta (s'ha comptat menys) o sobra (s'ha comptat més). */
export const STOCK_COUNT_LINE_STATES = ['pending', 'match', 'short', 'over'] as const;
export type StockCountLineState = (typeof STOCK_COUNT_LINE_STATES)[number];

/** El filtre de la pantalla: tots o un estat. */
export type StockCountLineFilter = 'all' | StockCountLineState;

/** El que es coneix d'una línia per jutjar-la. */
export interface CountLineFacts {
  /** La teòrica de la foto. Zero a un lot trobat. */
  readonly theoretical: number;
  /** La quantitat comptada, o null si encara no s'ha comptat. */
  readonly counted: number | null;
  /** La teòrica d'ARA (la que es llegirà en validar), o null mentre no es coneix (o és una línia nova sense lectura). */
  readonly current: number | null;
  /** La línia l'han afegida a mà (un lot trobat). */
  readonly found: boolean;
  /** Un lot que no existeix al sistema (en validar es crea): res a comparar, tot el que s'ha comptat «sobra». */
  readonly isNewLot: boolean;
}

/** `Math.round` a mil·lèsimes (la precisió de numeric(14,3)), sense soroll de coma flotant. */
export function roundQuantity(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Amb què es compara el que s'ha comptat: un lot nou, amb zero; si es coneix la teòrica d'ARA, amb aquesta (és la que fa servir
 * l'ajust); si no, amb la de la foto.
 */
export function countLineReference(line: CountLineFacts): number {
  if (line.isNewLot) return 0;
  return line.current ?? line.theoretical;
}

/** `comptat − referència` (la quantitat que s'ajustarà), o null si la línia no s'ha comptat. */
export function countLineDifference(line: CountLineFacts): number | null {
  if (line.counted === null) return null;
  return roundQuantity(line.counted - countLineReference(line));
}

export function countLineState(line: CountLineFacts): StockCountLineState {
  const difference = countLineDifference(line);
  if (difference === null) return 'pending';
  if (Math.abs(difference) <= STOCK_QUANTITY_EPSILON) return 'match';
  return difference < 0 ? 'short' : 'over';
}

/**
 * La teòrica d'ara no és la de la foto: algú ha mogut existències d'aquest lot en aquest lloc des que es va crear el recompte.
 * Només té sentit a una línia de la foto (una de trobada no en tenia).
 */
export function countLineChanged(line: CountLineFacts): boolean {
  if (line.found || line.isNewLot || line.current === null) return false;
  return Math.abs(line.current - line.theoretical) > STOCK_QUANTITY_EPSILON;
}

export function matchesCountLineFilter(
  state: StockCountLineState,
  filter: StockCountLineFilter,
): boolean {
  return filter === 'all' || state === filter;
}

/**
 * La quantitat que «Confirma la teòrica» posa a una línia: el saldo d'ARA quan es coneix i, mentre no es coneix, el de la foto.
 *
 * Per què el d'ara: «confirmar» vol dir «el que diu el sistema és correcte». Si algú ha mogut existències d'aquest lot des de la
 * foto (la foto diu 10, ara en queden 7), confirmar la FOTO comptaria 10 i, en validar (`comptat − saldo d'ara`), l'ajust seria +3:
 * desfaria un moviment legítim sense que ningú ho hagi comptat. Confirmar el d'ara deixa la diferència a zero. El servidor té el seu
 * propi `confirm_theoretical` (la foto) per a clients que no coneixen el saldo d'ara; el BackOffice envia aquesta quantitat explícita.
 *
 * Una línia de teòrica negativa no es pot confirmar (un saldo negatiu no és un recompte): s'ha de comptar de debò. Torna null quan
 * no es pot confirmar.
 */
export function confirmedQuantity(
  line: Pick<CountLineFacts, 'theoretical' | 'isNewLot'> & { readonly current?: number | null },
): number | null {
  if (line.isNewLot) return null;
  const reference = line.current ?? line.theoretical;
  return reference >= 0 ? roundQuantity(reference) : null;
}

/** Confirmar el saldo d'ara NO és confirmar la foto: la línia ha canviat i la pantalla ho ha de dir en confirmar. */
export function confirmUsesCurrent(
  line: Pick<CountLineFacts, 'theoretical' | 'isNewLot' | 'found'> & {
    readonly current?: number | null;
  },
): boolean {
  return countLineChanged({
    theoretical: line.theoretical,
    counted: null,
    current: line.current ?? null,
    found: line.found,
    isNewLot: line.isNewLot,
  });
}

// ── El resum ─────────────────────────────────────────────────────────────────────

export interface CountSummary {
  readonly lines: number;
  readonly counted: number;
  readonly pending: number;
  readonly match: number;
  readonly short: number;
  readonly over: number;
  /** Línies comptades que NO són la foto: la teòrica ha canviat des que es va crear el recompte. */
  readonly changed: number;
}

export function summarizeCount(lines: readonly CountLineFacts[]): CountSummary {
  let pending = 0;
  let match = 0;
  let short = 0;
  let over = 0;
  let changed = 0;
  for (const line of lines) {
    const state = countLineState(line);
    if (state === 'pending') pending += 1;
    else if (state === 'match') match += 1;
    else if (state === 'short') short += 1;
    else over += 1;
    if (state !== 'pending' && countLineChanged(line)) changed += 1;
  }
  return {
    lines: lines.length,
    counted: lines.length - pending,
    pending,
    match,
    short,
    over,
    changed,
  };
}

// ── Què passarà en validar ───────────────────────────────────────────────────────────

/** Què sap la pantalla d'una línia per predir si el servidor en rebutjarà l'ajust. */
export interface CountAdjustmentFacts extends CountLineFacts {
  /** La línia té un lot de proveïdor o de producció EXISTENT. */
  readonly hasLot: boolean;
  /** L'article demana caducitat als lots ({@link lotRequiresExpiry}). */
  readonly requiresExpiry: boolean;
  /** La caducitat que el lot ja té, o null. */
  readonly lotExpiryDate: string | null;
  /** La caducitat indicada a la línia, o null. */
  readonly expiryDate: string | null;
  /** L'article admet un lot nou d'ajust ({@link canCreateAdjustmentLot}). */
  readonly canCreateLot: boolean;
  /**
   * L'article encara porta control d'existències (i es veu): `false` quan s'ha deixat de controlar des de la foto. La teòrica d'ara
   * continua sent el saldo real, però `adjust_stock` ja no hi escriu (`adjust_stock_untracked`): una línia amb diferència no es pot
   * validar. Absent = es dona per bo (l'article no s'ha pogut llegir: el servidor ho decidirà).
   */
  readonly countable?: boolean;
}

/** Els errors que una línia pot donar en validar i que la pantalla pot predir (els mateixos codis que posa el servidor). */
export type CountLineIssue = Extract<
  StockAdjustErrorCode,
  | 'adjust_stock_lot_required'
  | 'adjust_stock_expiry_required'
  | 'adjust_stock_negative'
  | 'adjust_stock_untracked'
  | 'lot_requires_work_order'
>;

const PREDICTABLE_ISSUES: readonly CountLineIssue[] = [
  'adjust_stock_lot_required',
  'adjust_stock_expiry_required',
  'adjust_stock_negative',
  'adjust_stock_untracked',
  'lot_requires_work_order',
];

/**
 * D'entre els motius d'una validació d'ajust, els que un recompte pot predir: la quantitat, el motiu i la nota els posa el servidor
 * (la quantitat d'una línia comptada ja és vàlida per construcció), així que no es poden donar.
 */
function predictable(issues: readonly string[]): readonly CountLineIssue[] {
  return issues.filter((issue): issue is CountLineIssue =>
    (PREDICTABLE_ISSUES as readonly string[]).includes(issue),
  );
}

/**
 * Els motius pels quals el servidor rebutjaria l'ajust d'aquesta línia en validar-la (res si no s'ha comptat o si coincideix: no
 * s'hi escriu res). Amb les MATEIXES regles que `adjust_stock` i `create_adjustment_lot` (via `validateAdjustment` /
 * `validateAdjustmentLot`); el servidor continua tenint l'última paraula. El motiu i la nota els posa el servidor, per això aquí es
 * donen per escrits.
 */
export function predictCountLineIssues(line: CountAdjustmentFacts): readonly CountLineIssue[] {
  const difference = countLineDifference(line);
  if (difference === null || line.counted === null) return [];
  if (line.isNewLot) {
    if (line.counted <= STOCK_QUANTITY_EPSILON) return [];
    if (line.countable === false) return ['adjust_stock_untracked'];
    return predictable(
      validateAdjustmentLot({
        quantity: line.counted,
        reason: STOCK_COUNT_REASON,
        note: 'Recompte',
        requiresExpiry: line.requiresExpiry,
        expiryDate: line.expiryDate,
        canCreateLot: line.canCreateLot,
      }),
    );
  }
  if (Math.abs(difference) <= STOCK_QUANTITY_EPSILON) return [];
  if (line.countable === false) return ['adjust_stock_untracked'];
  return predictable(
    validateAdjustment({
      quantity: difference,
      reason: STOCK_COUNT_REASON,
      note: 'Recompte',
      hasSupplierLot: line.hasLot,
      hasFinishedLot: false,
      balance: line.current ?? line.theoretical,
      requiresExpiry: line.requiresExpiry,
      lotExpiryDate: line.lotExpiryDate,
      expiryDate: line.expiryDate,
    }),
  );
}

/** Una línia del pla d'ajustos: què s'hi escriurà. */
export interface PlannedAdjustment {
  readonly id: string;
  /** `increase`: afegeix; `decrease`: treu; `new_lot`: crea un lot nou amb la quantitat comptada. */
  readonly kind: 'increase' | 'decrease' | 'new_lot';
  /** La variació amb signe (en un lot nou, la quantitat comptada). */
  readonly quantity: number;
  readonly issues: readonly CountLineIssue[];
}

export interface CountAdjustmentPlan {
  /** Els ajustos que es crearan (un per línia comptada amb diferència). */
  readonly adjustments: readonly PlannedAdjustment[];
  /** Les línies comptades que coincideixen: no escriuen res. */
  readonly matched: number;
  /** Les línies sense comptar: no es toquen. */
  readonly notCounted: number;
  /** Les línies comptades la teòrica de les quals ha canviat des de la foto (un avís, mai un bloqueig). */
  readonly changed: number;
  /** Els ajustos que el servidor rebutjarà amb tota probabilitat (cal arreglar-los abans de validar). */
  readonly blocked: readonly PlannedAdjustment[];
}

/** El pla d'ajustos d'un recompte: per línia, què s'hi escriurà i si el servidor ho rebutjarà. */
export function planCountAdjustments(
  lines: ReadonlyArray<CountAdjustmentFacts & { readonly id: string }>,
): CountAdjustmentPlan {
  const adjustments: PlannedAdjustment[] = [];
  let matched = 0;
  let notCounted = 0;
  let changed = 0;
  for (const line of lines) {
    const difference = countLineDifference(line);
    if (difference === null) {
      notCounted += 1;
      continue;
    }
    if (countLineChanged(line)) changed += 1;
    if (line.isNewLot) {
      if ((line.counted ?? 0) <= STOCK_QUANTITY_EPSILON) {
        matched += 1;
        continue;
      }
      adjustments.push({
        id: line.id,
        kind: 'new_lot',
        quantity: line.counted ?? 0,
        issues: predictCountLineIssues(line),
      });
      continue;
    }
    if (Math.abs(difference) <= STOCK_QUANTITY_EPSILON) {
      matched += 1;
      continue;
    }
    adjustments.push({
      id: line.id,
      kind: difference > 0 ? 'increase' : 'decrease',
      quantity: difference,
      issues: predictCountLineIssues(line),
    });
  }
  return {
    adjustments,
    matched,
    notCounted,
    changed,
    blocked: adjustments.filter((a) => a.issues.length > 0),
  };
}

// ── Errors d'aplicació ───────────────────────────────────────────────────────────────

/** Els errors propis del recompte (el codi va al `message`). Els d'`adjust_stock` surten també de `validate_stock_count`. */
export const STOCK_COUNT_ERROR_CODES = [
  'stock_count_forbidden',
  'stock_count_bad_scope',
  'stock_count_too_large',
  'stock_count_client_uuid_conflict',
  'stock_count_not_found',
  'stock_count_not_open',
  'stock_count_bad_line',
  'stock_count_bad_quantity',
  'stock_count_unknown_line',
  'stock_count_unknown_article',
  'stock_count_unknown_lot',
  'stock_count_bad_place',
  'stock_count_out_of_scope',
  'stock_count_lot_required',
  'stock_count_reason_required',
  'stock_count_immutable',
] as const;
export type StockCountErrorCode = (typeof STOCK_COUNT_ERROR_CODES)[number];

/** Els errors d'`adjust_stock` / `create_adjustment_lot` que un recompte pot arrossegar (en validar o en afegir un lot trobat). */
export type StockCountForwardedErrorCode = StockAdjustErrorCode;

/** El codi d'aplicació d'un missatge d'error del recompte (propi o d'un ajust), o null si no se'n coneix cap. */
export function stockCountErrorCode(
  message: string | null | undefined,
): StockCountErrorCode | StockCountForwardedErrorCode | null {
  if (!message) return null;
  const own = STOCK_COUNT_ERROR_CODES.find((code) => message.includes(code));
  if (own !== undefined) return own;
  const forwarded = stockErrorCode(message);
  return forwarded !== null && (STOCK_ADJUST_ERROR_CODES as readonly string[]).includes(forwarded)
    ? (forwarded as StockAdjustErrorCode)
    : null;
}

/** La línia que ha causat l'error de validació (`<codi> line=<uuid>`), o null. */
export function stockCountErrorLineId(message: string | null | undefined): string | null {
  if (!message) return null;
  const match = /line=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(
    message,
  );
  return match?.[1] ?? null;
}

/** La clau i18n de la frase d'obrador de cada error propi. */
export const STOCK_COUNT_ERROR_I18N_KEYS: Readonly<Record<StockCountErrorCode, string>> = {
  stock_count_forbidden: 'backoffice.stockCounts:errForbidden',
  stock_count_bad_scope: 'backoffice.stockCounts:errBadScope',
  stock_count_too_large: 'backoffice.stockCounts:errTooLarge',
  stock_count_client_uuid_conflict: 'backoffice.stockCounts:errClientUuidConflict',
  stock_count_not_found: 'backoffice.stockCounts:errNotFound',
  stock_count_not_open: 'backoffice.stockCounts:errNotOpen',
  stock_count_bad_line: 'backoffice.stockCounts:errBadLine',
  stock_count_bad_quantity: 'backoffice.stockCounts:errBadQuantity',
  stock_count_unknown_line: 'backoffice.stockCounts:errUnknownLine',
  stock_count_unknown_article: 'backoffice.stockCounts:errUnknownArticle',
  stock_count_unknown_lot: 'backoffice.stockCounts:errUnknownLot',
  stock_count_bad_place: 'backoffice.stockCounts:errBadPlace',
  stock_count_out_of_scope: 'backoffice.stockCounts:errOutOfScope',
  stock_count_lot_required: 'backoffice.stockCounts:errLotRequired',
  stock_count_reason_required: 'backoffice.stockCounts:errReasonRequired',
  stock_count_immutable: 'backoffice.stockCounts:errImmutable',
};

export const STOCK_COUNT_ERROR_GENERIC_KEY = 'backoffice.stockCounts:errGeneric';

/**
 * La clau i18n del missatge d'un error de les RPC del recompte. Un error d'ajust (`adjust_stock_negative`…) usa la frase que ja
 * explica l'ajust; un codi que no es coneix surt amb la frase genèrica: cap error no es mostra mai cru.
 */
export function stockCountErrorKey(error: unknown): string {
  const message =
    typeof error === 'string' ? error : error instanceof Error ? error.message : undefined;
  const code = stockCountErrorCode(message);
  if (code === null) return STOCK_COUNT_ERROR_GENERIC_KEY;
  if ((STOCK_COUNT_ERROR_CODES as readonly string[]).includes(code)) {
    return STOCK_COUNT_ERROR_I18N_KEYS[code as StockCountErrorCode];
  }
  return STOCK_ERROR_I18N_KEYS[code as StockAdjustErrorCode] ?? STOCK_ERROR_GENERIC_KEY;
}

// ── L'App Obrador compta (OBR-149) ────────────────────────────────────────────────────

/**
 * Qui pot COMPTAR des de l'App: els que gestionen el recompte ({@link STOCK_COUNT_ROLES}) i l'operari (rol `operator`, només planta).
 * Mirall de la comprovació de rols de `save_stock_count_lines` i `add_stock_count_line` (migració 20261106100000): l'operari COMPTA
 * (desa quantitats i afegeix lots trobats) però no crea, valida ni cancel·la un recompte (`create_stock_count`, `validate_stock_count` i
 * `cancel_stock_count` no canvien). Un test llegeix la migració i comprova que la llista és la mateixa.
 */
export const STOCK_COUNT_COUNTER_ROLES = [
  ...STOCK_COUNT_ROLES,
  'operator',
] as const satisfies readonly UserRole[];

/** Una d'aquestes persones pot comptar un recompte obert des de l'App. */
export function canCountStock(roles: ReadonlyArray<UserRole>): boolean {
  return roles.some((role) => (STOCK_COUNT_COUNTER_ROLES as readonly UserRole[]).includes(role));
}

/** El topall de la nota d'una línia (`stock_count_lines_note_chk`; el servidor la talla a 500 caràcters). */
export const STOCK_COUNT_LINE_NOTE_MAX = 500;

/** El topall del codi de proveïdor d'un lot nou trobat (`stock_count_lines_new_lot_code_chk`). */
export const STOCK_COUNT_NEW_LOT_CODE_MAX = 120;

/** Una quantitat comptada ha de ser MENOR que això: és el que cap a `numeric(14,3)` (el servidor rebutja `stock_count_bad_quantity`). */
export const STOCK_COUNT_QUANTITY_LIMIT = 100_000_000_000;

/**
 * Una quantitat que el servidor acceptarà com a comptada: finita, no negativa i que càpiga a `numeric(14,3)` ARRODONIDA a mil·lèsimes
 * (`99999999999,9996` s'arrodoneix a 1e11 i desbordaria). Mirall de les comprovacions de `save_stock_count_lines` i `add_stock_count_line`.
 */
export function isCountableQuantity(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && roundQuantity(value) < STOCK_COUNT_QUANTITY_LIMIT;
}

/** L'abast d'un recompte tal com l'App el coneix (els identificadors que torna `stock_counts_for_app`). */
export interface StockCountScopeFacts {
  readonly scopeType: StockCountScopeType;
  readonly zoneId: string | null;
  readonly sectionId: string | null;
  readonly locationId: string | null;
  readonly articleId: string | null;
}

/** Un lloc ja NORMALITZAT com el llibre (ubicació → secció → zona): tot NUL = «sense lloc». */
export interface StockCountPlaceFacts {
  readonly zoneId: string | null;
  readonly sectionId: string | null;
  readonly locationId: string | null;
}

/**
 * Aquest article en aquest lloc és DINS de l'abast del recompte? Mirall de la comprovació d'`add_stock_count_line` (`stock_count_out_of_scope`):
 * tot l'obrador, sí; una zona, una secció o una ubicació, si el lloc (ja normalitzat) hi penja; un article, si és aquell article. Serveix a
 * l'App per jutjar un lot trobat ABANS d'enviar-lo (el servidor l'aparcaria mort) i per no oferir-ne un fora de l'abast.
 */
export function stockCountCovers(
  scope: StockCountScopeFacts,
  articleId: string,
  place: StockCountPlaceFacts,
): boolean {
  switch (scope.scopeType) {
    case 'tenant':
      return true;
    case 'zone':
      return scope.zoneId !== null && place.zoneId === scope.zoneId;
    case 'section':
      return scope.sectionId !== null && place.sectionId === scope.sectionId;
    case 'location':
      return scope.locationId !== null && place.locationId === scope.locationId;
    case 'article':
      return scope.articleId !== null && articleId === scope.articleId;
    default:
      return false;
  }
}

/**
 * El lloc on es posa un lot trobat quan no se'n coneix un de millor: el de l'abast si l'abast és un lloc (zona, secció o ubicació) i, si és
 * tot l'obrador o un article, «sense lloc». El servidor el normalitza (una secció dona la seva zona).
 */
export function stockCountScopePlace(scope: StockCountScopeFacts): StockCountPlaceFacts {
  switch (scope.scopeType) {
    case 'zone':
      return { zoneId: scope.zoneId, sectionId: null, locationId: null };
    case 'section':
      return { zoneId: null, sectionId: scope.sectionId, locationId: null };
    case 'location':
      return { zoneId: null, sectionId: null, locationId: scope.locationId };
    default:
      return { zoneId: null, sectionId: null, locationId: null };
  }
}

/**
 * Quan va ser un instant, al rellotge de l'OBRADOR (`OBRADOR_TIME_ZONE`, hora d'estiu inclosa), sigui quina sigui la zona de l'aparell: el dia
 * (`YYYY-MM-DD`) i l'hora (`HH:mm`). Serveix per dir quan es va fer la foto d'un recompte: «2026-11-03T23:30:00Z» és el 4/11 a les 00:30 a Madrid, no el
 * 3/11 (el dia UTC) ni la hora local d'un aparell fora de la zona. Mateixa tècnica que `todayInObradorZone` (només `Intl.DateTimeFormat#format`, amb el
 * calendari `sv-SE`; mai `formatToParts`: el Hermes d'iOS no el té) i, com ell, amb el `en-US` de reserva. Un instant no llegible torna null.
 */
export function obradorMoment(iso: string): { readonly day: string; readonly time: string } | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const options: Intl.DateTimeFormatOptions = {
    timeZone: OBRADOR_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  const sv = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})$/.exec(new Intl.DateTimeFormat('sv-SE', options).format(at));
  if (sv !== null) return { day: sv[1] as string, time: `${sv[2]}:${sv[3]}` };
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),? (\d{2}):(\d{2})$/.exec(new Intl.DateTimeFormat('en-US', options).format(at));
  if (us === null) return null;
  return {
    day: `${us[3]}-${(us[1] as string).padStart(2, '0')}-${(us[2] as string).padStart(2, '0')}`,
    time: `${us[4]}:${us[5]}`,
  };
}
